import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  IconButton,
  AppBar,
  Toolbar,
  Avatar,
  List,
  ListItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress
} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import SendIcon from '@mui/icons-material/Send';
import { useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import { therapistClient, secureClient } from '../apiClient';

const FreeChat = () => {
  const { chat_uuid } = useParams();
  const navigate = useNavigate();
  const [newMessage, setNewMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [socket, setSocket] = useState(null);
  const chatId = chat_uuid;
  const messagesEndRef = useRef(null);

  // Chat info state
  const [chatInfo, setChatInfo] = useState({
    therapist_name: '',
    therapist_uuid: '',
    patient_uuid: null
  });

  // Registration dialog state
  const [showRegistration, setShowRegistration] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [registrationLoading, setRegistrationLoading] = useState(false);
  const [registrationError, setRegistrationError] = useState(null);

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fetch chat information when component mounts
  useEffect(() => {
    const fetchChatInfo = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const response = await secureClient.get(`/api/public/chats/${chatId}`);
        
        // Update chat info
        setChatInfo({
          therapist_name: response.data.therapist_name || 'Your Therapist',
          therapist_uuid: response.data.therapist_uuid,
          patient_uuid: response.data.patient_uuid || null
        });
        
        // If messages exist, set them
        if (response.data.messages && response.data.messages.length > 0) {
          setMessages(response.data.messages);
        }
        
        // If no patient UUID exists, show registration dialog
        if (!response.data.patient_uuid) {
          setShowRegistration(true);
        }
        
      } catch (err) {
        console.error('Error fetching chat information:', err);
        setError('Failed to load chat. The link may be invalid or expired.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchChatInfo();
  }, [chatId]);

  // Set up socket connection when patient_uuid is available
  useEffect(() => {
    // Only connect if we have a patient UUID
    if (!chatInfo.patient_uuid) return;

    // Connect to the main namespace instead of patient namespace
    const newSocket = io({
      withCredentials: true,
      transports: ['websocket', 'polling'],
      reconnectionAttempts: process.env.NODE_ENV === 'development' ? 3 : 10
    });
    
    newSocket.on('connect', () => {
      console.log('Socket connected');
      
      // Send identification to socket server using the patient-specific event
      newSocket.emit('identify_patient', {
        patient_uuid: chatInfo.patient_uuid,
        chat_uuid: chatId
      });
    });
    
    newSocket.on('identified', (data) => {
      console.log('Patient identified:', data);
    });
    
    // Listen for new messages
    newSocket.on('new_message', (msg) => {
      console.log('New message received:', msg);
      
      // Only add message to state if it's from the therapist
      // or if it's from another source (not this patient)
      if (msg.therapist_uuid || (msg.patient_uuid && msg.patient_uuid !== chatInfo.patient_uuid)) {
        const formattedMessage = {
          id: msg.message_uuid,
          content: msg.content,
          sender: msg.therapist_uuid ? 'therapist' : 'patient',
          timestamp: new Date(msg.created_at).toLocaleTimeString([], { 
            hour: '2-digit', 
            minute: '2-digit' 
          })
        };
        
        setMessages(prev => [...prev, formattedMessage]);
      }
    });
    
    // Confirmation after message is sent
    newSocket.on('message_sent', (msg) => {
      console.log('Message confirmed:', msg);
    });
    
    // Handle errors
    newSocket.on('error', (err) => {
      console.error('Socket error:', err);
      // Could add UI notification here
    });
    
    newSocket.on('disconnect', () => {
      console.log('Socket disconnected');
    });
    
    // Save socket to state
    setSocket(newSocket);
    
    // Clean up function
    return () => {
      console.log('Disconnecting socket');
      newSocket.disconnect();
    };
  }, [chatInfo.patient_uuid, chatId, chatInfo.therapist_uuid]);

  // Handle patient registration
  const handleRegister = async () => {
    // Validate form
    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      setRegistrationError('Please fill in all fields');
      return;
    }
    
    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setRegistrationError('Please enter a valid email address');
      return;
    }
    
    setRegistrationLoading(true);
    setRegistrationError(null);
    
    try {
      // Register patient and link to this chat
      const response = await secureClient.post(`/api/public/chats/${chatId}/register`, {
        first_name: firstName,
        last_name: lastName,
        email: email
      });
      
      // Update chat info with new patient UUID
      setChatInfo(prev => ({
        ...prev,
        patient_uuid: response.data.patient_uuid
      }));
      
      // Add welcome message from therapist
      const welcomeMessage = {
        id: Date.now().toString(),
        content: `Hello ${firstName}! I'm glad you're here. How can I help you today?`,
        sender: 'therapist',
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      
      setMessages([welcomeMessage]);
      
      // Close registration dialog
      setShowRegistration(false);
      
    } catch (err) {
      console.error('Error registering patient:', err);
      setRegistrationError('Failed to register. Please try again.');
    } finally {
      setRegistrationLoading(false);
    }
  };

  // Handle sending a new message
  const handleSendMessage = async () => {
    if (newMessage.trim() === '') return;
    
    // Create new message object for UI
    const newMsg = {
      id: Date.now().toString(),
      content: newMessage,
      sender: 'patient',
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
    
    // Keep track of the message content before clearing
    const messageContent = newMessage;
    
    // Optimistically add message to UI
    setMessages(prev => [...prev, newMsg]);
    setNewMessage('');
    
    try {
      // Try to send via socket if available
      if (socket && socket.connected) {
        socket.emit('send_patient_message', {
          content: messageContent,
          message_type: 'text'
        });
      } else {
        // Fallback to HTTP if socket isn't available
        await secureClient.post(`/api/public/chats/${chatId}/messages`, {
          content: messageContent,
          patient_uuid: chatInfo.patient_uuid
        });
      }
    } catch (err) {
      console.error('Error sending message:', err);
      // You could add an error indicator to the message or show a notification
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Show loading state
  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: 2
      }}>
        <CircularProgress />
        <Typography variant="body1">Loading chat...</Typography>
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: 2,
        p: 3,
        textAlign: 'center'
      }}>
        <Typography variant="h5" color="error">Error</Typography>
        <Typography variant="body1">{error}</Typography>
        <Button 
          variant="contained" 
          onClick={() => navigate('/')}
          sx={{ mt: 2 }}
        >
          Go to Homepage
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      display: "flex", 
      flexDirection: "column", 
      height: "100vh", 
      bgcolor: "#f5f5f5" 
    }}>
      {/* Header */}
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ChatIcon sx={{ color: 'primary.main' }} />
            <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
              Chat with {chatInfo.therapist_name}
            </Typography>
          </Box>
          {/* Socket status indicator */}
          <Box sx={{ 
            ml: 'auto', 
            display: 'flex', 
            alignItems: 'center', 
            gap: 0.5 
          }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: socket && socket.connected ? 'success.main' : 'error.main'
              }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              {socket && socket.connected ? 'Connected' : 'Offline'}
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Chat Area */}
      <Box sx={{ 
        flex: 1, 
        overflowY: 'auto', 
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 2
      }}>
        <Container maxWidth="md">
          {messages.length === 0 ? (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: '100%',
              flexDirection: 'column',
              gap: 2,
              p: 5
            }}>
              <Typography variant="body1" color="text.secondary">
                No messages yet. Start the conversation by typing below.
              </Typography>
            </Box>
          ) : (
            <List sx={{ width: '100%', p: 0 }}>
              {messages.map((message) => (
                <ListItem 
                  key={message.id}
                  sx={{
                    display: 'flex',
                    justifyContent: message.sender === 'patient' ? 'flex-end' : 'flex-start',
                    p: 0.5
                  }}
                >
                  {message.sender === 'therapist' && (
                    <Avatar 
                      sx={{ 
                        mr: 1,
                        bgcolor: 'primary.main',
                        display: { xs: 'none', sm: 'flex' }
                      }}
                    >
                      {chatInfo.therapist_name.split(' ').map(n => n[0]).join('')}
                    </Avatar>
                  )}
                  
                  <Paper
                    elevation={message.sender === 'therapist' ? 1 : 0}
                    sx={{
                      bgcolor: message.sender === 'patient' ? 'primary.light' : '#fff',
                      color: message.sender === 'patient' ? 'primary.contrastText' : 'text.primary',
                      px: 2,
                      py: 1.5,
                      maxWidth: { xs: '85%', md: '70%' },
                      borderRadius: 2,
                      position: 'relative'
                    }}
                  >
                    <Typography variant="body1">{message.content}</Typography>
                    <Typography 
                      variant="caption" 
                      color={message.sender === 'patient' ? 'rgba(255,255,255,0.8)' : 'text.secondary'}
                      sx={{ display: 'block', mt: 0.5 }}
                    >
                      {message.timestamp}
                    </Typography>
                  </Paper>
                  
                  {message.sender === 'patient' && (
                    <Avatar 
                      sx={{ 
                        ml: 1,
                        bgcolor: 'secondary.main',
                        display: { xs: 'none', sm: 'flex' }
                      }}
                    >
                      ME
                    </Avatar>
                  )}
                </ListItem>
              ))}
              {/* Invisible element to scroll to */}
              <div ref={messagesEndRef} />
            </List>
          )}
        </Container>
      </Box>

      {/* Message Input */}
      <Paper 
        elevation={3} 
        square
        sx={{ 
          p: 2, 
          borderTop: '1px solid rgba(0, 0, 0, 0.12)' 
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              variant="outlined"
              placeholder="Type your message..."
              fullWidth
              multiline
              maxRows={4}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: '#fafafa'
                }
              }}
            />
            <IconButton 
              color="primary" 
              onClick={handleSendMessage}
              disabled={newMessage.trim() === ''}
              sx={{ 
                p: 1.5, 
                backgroundColor: 'primary.main', 
                color: 'white',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
                '&.Mui-disabled': {
                  backgroundColor: 'action.disabledBackground',
                  color: 'action.disabled'
                }
              }}
            >
              <SendIcon />
            </IconButton>
          </Box>
        </Container>
      </Paper>

      {/* Registration Dialog */}
      <Dialog 
        open={showRegistration} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: '#065e18', 
          color: '#f5f3e6',
          padding: '16px 24px'
        }}>
          Welcome to Chat
        </DialogTitle>
        <DialogContent sx={{ 
          backgroundColor: '#065e18', 
          color: '#f5f3e6',
          py: 3
        }}>
          <Typography variant="body1" sx={{ mb: 3, color: '#f5f3e6' }}>
            Please provide your information to start chatting with {chatInfo.therapist_name}
          </Typography>
          
          {registrationError && (
            <Typography color="error" variant="body2" sx={{ mb: 2, bgcolor: 'rgba(255, 255, 255, 0.9)', p: 1, borderRadius: 1 }}>
              {registrationError}
            </Typography>
          )}
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {/* First and Last Name in a row */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="First Name"
                variant="outlined"
                fullWidth
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  },
                  '& .MuiInputLabel-root': {
                    color: '#065e18'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#f5f3e6'
                  },
                  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#f5f3e6'
                  },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#f5f3e6'
                  }
                }}
              />
              <TextField
                label="Last Name"
                variant="outlined"
                fullWidth
                required
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  },
                  '& .MuiInputLabel-root': {
                    color: '#065e18'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#f5f3e6'
                  },
                  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#f5f3e6'
                  },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#f5f3e6'
                  }
                }}
              />
            </Box>
            <TextField
              label="Email"
              type="email"
              variant="outlined"
              fullWidth
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: 'rgba(255, 255, 255, 0.9)'
                },
                '& .MuiInputLabel-root': {
                  color: '#065e18'
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#f5f3e6'
                },
                '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#f5f3e6'
                },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#f5f3e6'
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          backgroundColor: '#065e18', 
          p: 2
        }}>
          <Button 
            onClick={() => navigate('/')} 
            disabled={registrationLoading}
            sx={{
              color: '#f5f3e6',
              '&:hover': {
                backgroundColor: 'rgba(245, 243, 230, 0.1)'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleRegister}
            disabled={registrationLoading}
            startIcon={registrationLoading ? <CircularProgress size={20} /> : null}
            sx={{ 
              backgroundColor: '#f5f3e6', 
              color: '#065e18',
              '&:hover': {
                backgroundColor: '#e5e3d6',
              },
              '&.Mui-disabled': {
                backgroundColor: 'rgba(245, 243, 230, 0.5)',
                color: 'rgba(6, 94, 24, 0.7)'
              }
            }}
          >
            {registrationLoading ? 'Registering...' : 'Start Chat'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FreeChat;